import config from '@/common/config';
import { Unit, UnitOfflineStatus } from '@/common/global.interfaces';
import { getDateTimeDifference } from '@/common/helpers/date-utils';
import { Http } from '@samknows/utils';
import dayjs from 'dayjs';
import { getTimeSinceOnline } from '../helpers/offline.helpers';

export const baseUnitOfflineStatus: UnitOfflineStatus = {
  offline: false,
  lastSeenOnline: null,
  offlineMessage: '',
  id: null,
  isUnreliable: false
};

export interface DisconnectionInProgress {
  dtime: string;
  endDtime: string;
  source: string;
  metricValue: unknown;
  isUnreliable: boolean;
}

interface DisconnectionsInProgressResponse {
  data: {
    results: DisconnectionInProgress[];
  };
}

export const MAX_UNIT_LAST_SEEN_DURATION_DAYS = 30;
export const VIRGIN_MEDIA_UK_PANEL_ID = 3;

export const UnitOfflineService = {
  async getUnitOfflineStatus(
    unit: Pick<Unit, 'id' | 'environment'>
  ): Promise<UnitOfflineStatus> {
    const unitId = unit.id;
    let lastSeenOnline = null;
    let isOffline = false;
    const lastSeen = unit.environment?.data?.last_seen || null;

    if (lastSeen) {
      const lastSeenDate = dayjs.unix(lastSeen);
      const now = dayjs();
      const diffInDays = getDateTimeDifference(now, lastSeenDate);
      const lastSeenBeyondThreshold =
        diffInDays > MAX_UNIT_LAST_SEEN_DURATION_DAYS;

      if (lastSeenBeyondThreshold) {
        lastSeenOnline = lastSeenDate;
        isOffline = true;
      }
    }

    const disconnectionInProgress = await this._getDisconnectionInProgress(
      unitId
    );

    let isUnreliable = false;
    if (disconnectionInProgress) {
      const disconnectionTime = disconnectionInProgress.dtime
        ? dayjs(disconnectionInProgress.dtime)
        : null;

      if (disconnectionTime) {
        lastSeenOnline = disconnectionTime;
      }

      isOffline = true;
      isUnreliable = !!disconnectionInProgress.isUnreliable;
    }

    if (isOffline) {
      const offlineMessage = getTimeSinceOnline(lastSeenOnline);
      return {
        ...baseUnitOfflineStatus,
        lastSeenOnline,
        offline: isOffline,
        offlineMessage,
        id: unitId,
        isUnreliable
      };
    } else {
      return {
        ...baseUnitOfflineStatus,
        lastSeenOnline,
        id: unitId
      };
    }
  },

  async _getDisconnectionInProgress(
    unitId: number | undefined
  ): Promise<DisconnectionInProgress | null> {
    if (!unitId) {
      return null;
    }

    const disconnectionInProgressRes: DisconnectionsInProgressResponse =
      await Http.get(
        config.api.unitAnalytics,
        `/${unitId}/disconnection_in_progress`
      );

    return disconnectionInProgressRes?.data?.results?.[0] ?? null;
  }
};

export default UnitOfflineService;
